import React, { useState, useEffect } from "react";
import { v4 as uuid } from "uuid";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import actions from "../Store/actions";
import { ActionBar } from "../common/components";
import makeStyles from "@mui/styles/makeStyles";
import styles from "./styles";
import {
    Box,
    Button,
    Typography,
    Grid,
    Alert,
    AlertTitle,
} from "@mui/material/";
import CheckIcon from "@mui/icons-material/Check";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import AssetAttributes from "./components/AssetAttributes";
import AssetJourney from "./AssetJourney";
import useAuditAssetLocations from "./useAuditAssetLocations";
import {
    useAuditAssignment,
    useAuditProgressDetails,
    useCurrentAuditAssignment,
    useCurrentLocation,
    useLinkAssetToOriginalAudits,
    useAssetStatus,
    useAuditBranch,
    useToaster,
} from "../common/hooks";
import CheckedAssetInfo from "./CheckedAssetInfo";
import { logToSentry } from "../config/Sentry";
import PlaceIcon from "@mui/icons-material/Place";

const AssetDetails = ({ assetMode = "" }) => {
    const [loading, setLoading] = useState(false);
    const [actionButtons, setActionButtons] = useState(true);
    const [expandDetails, setExpandDetails] = useState(
        assetMode === "" ? true : false
    );
    const [mode, setMode] = useState(assetMode); // cater exclude all mode
    const [updateAsset, setUpdateAsset] = useState(false);
    const classes = makeStyles(styles)();

    let navigate = useNavigate();
    const location = useLocation();

    const dispatch = useDispatch();
    const toast = useToaster();
    const { t } = useTranslation();
    const { auditId, assetId } = useParams();
    const isExcludeAll = !assetId;

    const {
        app,
        auditAssets,
        audits,
        assetDetails,
        auditAssignments,
        auditUpload,
    } = useSelector((state) => state);

    const currentLocation = useCurrentLocation();

    const isGroupAudit = audits.item.auditReference.toLowerCase() !== "none";

    const { imageSyncEnabled, netConnected } = app;
    const {
        photos,
        exclusionReasonTypeId,
        notes,
        exclusionReasonDate,
        excludeAssets,
    } = assetDetails;
    const asset = auditAssets.item;
    const linkAssetToSameAudit = useLinkAssetToOriginalAudits();
    const currentAuditAssignment = useCurrentAuditAssignment(auditId);
    const { required } = useAuditProgressDetails("db", auditId);

    const assetOfSameGroup =
        parseInt(currentAuditAssignment.auditLocation.audit.auditReference) ===
        parseInt(asset && asset.auditLocation.audit.auditReference)
            ? true
            : false;

    const auditAssignment =
        linkAssetToSameAudit && assetOfSameGroup
            ? currentAuditAssignment
            : useAuditAssignment({
                  auditLocationId: asset ? asset.auditLocation.id : null, // asset auditLocation,
                  assetOfSameGroup: assetOfSameGroup, // asset is of same group
                  assetAuditReference:
                      asset?.auditLocation.audit.auditReference, // audit reference of audit
                  currentAuditLocation:
                      currentAuditAssignment?.auditLocation.location.id, // current audit Location,
                  isGroupAudit,
              });

    /** Check if the asset is linked */
    const isLinked = location.state && location.state.linked;

    /**Get status ids as hooks cannot be called inside a function
     * Seen asset status id is 2
     * Exclude asset status id is 3
     */

    const seenStatusId = useAssetStatus(2) && useAssetStatus(2).id;
    const excludeStatusId = useAssetStatus(3) && useAssetStatus(3).id;

    useAuditAssetLocations(assetId, auditId);
    const branch = useAuditBranch(auditAssignment);

    useEffect(() => {
        // for exclude-all asset is not available
        if (!asset) return;
        dispatch(
            actions.assetDetails.create.set({
                assetTypeId: asset.assetCore.type,
            })
        );
    }, [asset, auditId, assetId]);

    useEffect(() => {
        // Checked Asset
        if (asset && asset.isAssigned) {
            setMode("update");
            dispatch(
                actions.assetAuditors.create.getRequest({ auditId, assetId })
            );
            setActionButtons(true);
        }
        // Unchecked asset
        if (asset && !asset.isAssigned) {
            setActionButtons(true);
            setMode("unchecked");
        }
        if (asset && assetMode === "exclude") {
            setActionButtons(false);
            setMode("exclude");
        }
        // for exclude-all asset is not available
        if (!assetId) {
            setMode("exclude");
            setActionButtons(false);
        }
    }, [assetMode, auditId, assetId, isExcludeAll]);

    const hdlExpandDetails = (flag) => {
        setExpandDetails(flag);
    };

    const hdlSetMode = (newMode) => () => {
        try {
            if (updateAsset) {
                newMode.includes("exclude")
                    ? setMode(`update${newMode}`)
                    : setMode(`updatechecked`);
            } else setMode(newMode);

            setActionButtons(false);
            setExpandDetails(false);
        } catch (error) {
            console.log(error);
            logToSentry("changing asset mode", {
                error,
            });
        }
    };

    /** On checking  asset */
    const onCheckAssetPress = async () => {
        setLoading(true);
        if (!auditUpload[auditId] || !auditUpload[auditId].firstAssetChecked)
            await dispatch(
                actions.auditUpload.create.update({
                    [auditId]: {
                        auditSignature: auditUpload[auditId]
                            ? {
                                  ...auditUpload[auditId].auditSignature,
                              }
                            : auditUpload.auditSignature,
                        firstAssetChecked: new Date().toISOString(),
                    },
                })
            );

        try {
            const data = {
                id: uuid(),
                assignedDate: new Date().toISOString(),
                auditAsset: {
                    id: parseInt(assetId),
                    isAssigned: true,
                },
                status: seenStatusId,
                exclusionReason: null,
                auditAssignment,
                auditImages: photos.filter(
                    (item) => Object.keys(item).length > 0 // send non empty objects - not all are mandatory
                ),
                assetSubmitted: false,
                assetSynced: false,
                notes: notes.content
                    ? [
                          {
                              ...notes,
                              createdDate: new Date().toISOString(),
                          },
                      ]
                    : [],
            };
            const linkedAuditId = data.auditAssignment.auditLocation.audit.id;

            // /** Make sure the data is stored as before in the db */
            await dispatch(
                actions.assetAuditors.create.writeRequest(
                    [data],
                    seenStatusId /** For asset status  */
                )
            );

            /**
             * Do a submission
             * 1. Net connected && Image sync enabled
             * 2. Net not connected && isLinked Asset
             * */

            if (netConnected && imageSyncEnabled) {
                const audId = isLinked ? linkedAuditId : parseInt(auditId);
                const assetChecked = isLinked
                    ? null
                    : audits.item.firstAssetChecked;
                dispatch(
                    actions.auditUpload.create.partialSubmitRequest(
                        linkAssetToSameAudit && assetOfSameGroup
                            ? parseInt(auditId)
                            : audId,
                        assetChecked,
                        required
                    )
                );
            } else if (!netConnected && isLinked) {
                const { linkedAudits } = auditUpload;
                if (!linkedAudits.includes(linkedAuditId)) {
                    const linkedAuditsIds = [...linkedAudits, linkedAuditId];
                    dispatch(
                        actions.auditUpload.create.linkedAssetStoreAuditId(
                            linkedAuditsIds
                        )
                    );
                }
            }

            dispatch(actions.assetDetails.create.clear());
            setLoading(false);
            navigate(`/auth/audits/${auditId}/assets/list`);
        } catch (error) {
            console.log(error);
            logToSentry(`${mode} Asset:`, {
                error,
            });
            toast.error(t("errors.checkAsset"));
            setLoading(false);
        }
    };

    /** On exlcuding  asset */
    const onExcludeClick = async () => {
        setLoading(true);
        try {
            const assetAuditor = {
                assignedDate: new Date().toISOString(),
                exclusionReason: exclusionReasonTypeId,
                assetSubmitted: false,
                assetSynced: false,
                status: excludeStatusId,
                notes: notes.content
                    ? [{ ...notes, createdDate: new Date().toISOString() }]
                    : [],
            };
            const getAuditAssignment = (auditAsset: any) => {
                return auditAssignments.data.find(
                    (item) =>
                        item.auditLocation.id === auditAsset.auditLocation.id
                );
            };

            let data,
                url = `/auth/audits/${auditId}/assets/list`;
            if (isExcludeAll) {
                /** Excluding all asset */
                const uncheckedAssets = auditAssets.data.filter(
                    (item) => !item.isAssigned && item.isMandatory
                );
                data = excludeAssets.reduce((acc, item) => {
                    if (!item.isAssigned && item.isMandatory) {
                        acc.push({
                            ...item,
                            ...assetAuditor,
                            auditAsset: {
                                id: item.id,
                                isAssigned: true,
                            },
                            exclusionReasonDate,
                            auditImages: photos.filter(
                                (item) => Object.keys(item).length > 0 // send non empty objects - not all are mandatory
                            ) /**Images won't be captured many */,
                            auditAssignment: getAuditAssignment(item),
                            id: uuid(),
                        });
                    }
                    return acc;
                }, []);
            } else {
                /** Excluding single asset */

                const assignementData = getAuditAssignment(asset);
                if (!assignementData) {
                    const error = {
                        message: `Failed to getAuditAssignment for:${JSON.stringify(
                            asset
                        )}`,
                    };
                    logToSentry(`onExcludeClick:`, error);
                    toast.error(t("errors.excludeAsset"));
                    return;
                }

                data = [
                    {
                        ...assetAuditor,
                        auditAsset: { id: parseInt(assetId), isAssigned: true },
                        auditAssignment:
                            linkAssetToSameAudit && assetOfSameGroup
                                ? auditAssignment
                                : assignementData,
                        exclusionReasonDate,
                        auditImages: photos.filter(
                            (item) => Object.keys(item).length > 0 // send non empty objects - not all are mandatory
                        ),
                        id: uuid(),
                    },
                ];
            }

            // Add first asset checked
            if (
                !auditUpload[auditId] ||
                !auditUpload[auditId].firstAssetChecked
            )
                await dispatch(
                    actions.auditUpload.create.update({
                        [auditId]: {
                            auditSignature: {
                                ...auditUpload[auditId].auditSignature,
                            },
                            firstAssetChecked: new Date().toISOString(),
                        },
                    })
                );

            /** Writing the data to asset auditor */
            await dispatch(
                actions.assetAuditors.create.writeRequest(
                    data,
                    excludeStatusId /** For asset status  */
                )
            );

            const linkedAuditId =
                data[0].auditAssignment.auditLocation.audit.id;
            /**
             * Do a partial sync
             * 1. Net connected 2. Image sync enabled 3. Asset is not linked
             * */
            if (netConnected && imageSyncEnabled) {
                const audId = isLinked ? linkedAuditId : parseInt(auditId);
                const assetChecked = isLinked
                    ? null
                    : audits.item.firstAssetChecked;
                dispatch(
                    actions.auditUpload.create.partialSubmitRequest(
                        linkAssetToSameAudit && assetOfSameGroup
                            ? parseInt(auditId)
                            : audId,
                        assetChecked,
                        required
                    )
                );
            } else if (!netConnected && isLinked) {
                const linkedAuditId =
                    data[0].auditAssignment.auditLocation.audit.id;
                const { linkedAudits } = auditUpload;
                if (!linkedAudits.includes(linkedAuditId)) {
                    const linkedAuditsIds = [...linkedAudits, linkedAuditId];
                    dispatch(
                        actions.auditUpload.create.linkedAssetStoreAuditId(
                            linkedAuditsIds
                        )
                    );
                }
            }

            navigate(url);
        } catch (error) {
            console.error(error);
            logToSentry(`${mode} Asset:`, {
                error,
            });
            toast.error(t("errors.excludeAsset"));
            setLoading(false);
        }
    };

    /** On update asset change the message and show the original action button */
    const onUpdateAsset = () => {
        try {
            setMode("checked");
            setUpdateAsset(true);
        } catch (error) {
            console.log(error);
            logToSentry(`Update Asset:`, {
                error,
            });
            toast.error(t("errors.updatingAsset"));
        }
    };

    /** On cancel update go back to search list  */
    const onCancelUpdate = () => {
        try {
            navigate(`/auth/audits/${auditId}/assets/list`);
        } catch (error) {
            console.error(error);
            logToSentry(`Cancel update:`, {
                error,
            });
            setLoading(false);
        }
    };

    return (
        <>
            {isGroupAudit && currentLocation.length > 0 && (
                <Alert
                    severity="info"
                    iconMapping={{
                        info: <PlaceIcon fontSize="inherit" />,
                    }}
                    sx={{ mb: 1 }}
                >
                    {currentLocation}
                </Alert>
            )}
            {/*  If the asset is linked */}
            {isLinked && (
                <Alert severity="info">
                    <AlertTitle>Info</AlertTitle>
                    {t("messages.assetLinked", { branch })}
                </Alert>
            )}
            {/*  If the asset is checked */}
            {asset && asset.isAssigned && !isExcludeAll ? (
                <CheckedAssetInfo />
            ) : null}
            {/*  Asset attributes */}
            {!isExcludeAll ? (
                <AssetAttributes
                    asset={asset}
                    expandDetails={expandDetails}
                    hdlExpandDetails={hdlExpandDetails}
                />
            ) : (
                <Box sx={{ pt: 3 }} />
            )}
            <AssetJourney
                assetMode={mode}
                actionBtnUpdate={actionButtons}
                loading={loading}
                hdlCheckAsset={onCheckAssetPress}
                hdlExcludeAsset={onExcludeClick}
            />
            {actionButtons && (
                <ActionBar>
                    <Grid container className={classes.yesNoActions}>
                        {mode !== "update" ? (
                            <Grid item xs={12}>
                                <Typography
                                    variant="body2"
                                    component="div"
                                    style={{ paddingBottom: ".5rem" }}
                                >
                                    {t("assetIsHere")}
                                </Typography>
                            </Grid>
                        ) : null}
                        <Grid container spacing={2}>
                            <>
                                <Grid item xs={6}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={loading}
                                        fullWidth
                                        onClick={
                                            mode === "update"
                                                ? onUpdateAsset
                                                : hdlSetMode("unchecked")
                                        } /* onModalShowCamera */
                                        endIcon={<CheckIcon />}
                                        disableElevation
                                    >
                                        {mode === "update"
                                            ? t("updateAsset")
                                            : t("yes")}
                                    </Button>
                                </Grid>
                                <Grid item xs={6}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        disabled={loading}
                                        fullWidth
                                        onClick={
                                            mode === "update"
                                                ? onCancelUpdate
                                                : hdlSetMode("exclude")
                                        } /* onExcludePress */
                                        endIcon={<HighlightOffIcon />}
                                        disableElevation
                                    >
                                        {mode === "update"
                                            ? t("back")
                                            : t("no")}
                                    </Button>
                                </Grid>
                            </>
                        </Grid>
                    </Grid>
                </ActionBar>
            )}
        </>
    );
};

export default AssetDetails;
